import React, { useState, useEffect } from "react";
import { trackShipmentByAwb } from "../../api/trackingService";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../assets/css/theme.css";
import "../../assets/css/orders.css";
import type { TrackingData } from "../../types";

interface TrackingModalProps {
    awbCode: string;
    orderId: number | string;
    onClose: () => void;
}

const TrackingModal = ({ awbCode, orderId, onClose }: TrackingModalProps) => {
    const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const token = localStorage.getItem("authToken");

    const formatDateTime = (dateString?: string): string => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const day = date.getDate();
            const month = months[date.getMonth()];
            const year = date.getFullYear();
            const hours = date.getHours();
            const minutes = date.getMinutes();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const formattedHours = hours % 12 || 12;
            const formattedMinutes = minutes < 10 ? '0' + minutes : minutes;
            
            return `${day} ${month} ${year}, ${formattedHours}:${formattedMinutes} ${ampm}`;
        } catch (e) {
            return dateString;
        }
    };

    const fetchTracking = async () => {
        setLoading(true);
        setError("");

        const result = await trackShipmentByAwb(awbCode, token);

        if (result.success) {
            setTrackingData(result.data);
        } else {
            setError(result.error);
        }

        setLoading(false);
    };

    useEffect(() => {
        fetchTracking();
    }, [awbCode]);

    const getStatusIcon = (status?: string): string => {
        if (!status) return "🚚";
        const statusLower = status.toLowerCase();
        if (statusLower.includes("delivered")) return "✅";
        if (statusLower.includes("transit") || statusLower.includes("shipped")) return "🚚";
        if (statusLower.includes("picked")) return "📦";
        if (statusLower.includes("pending")) return "⏳";
        return "🚚";
    };

    const isDelivered = trackingData?.shipment_summary?.current_status?.toLowerCase().includes("delivered");

    const summary = trackingData?.shipment_summary;
    const timeline = trackingData?.tracking_timeline || [];

    const DetailBox = ({ icon, label, value, wide = false }: { icon: string; label: string; value?: string | number | null; wide?: boolean }) => (
        <div className={`ffo-detail${wide ? " ffo-detail--wide" : ""}`}>
            <i className={`bi ${icon}`} />
            <div>
                <small>{label}</small>
                <strong>{value || "N/A"}</strong>
            </div>
        </div>
    );

    return (
        <div
            className="ffo-modal-backdrop"
            onClick={onClose}
        >
            <div
                className="ffo-modal ffo-modal--wide"
                role="dialog"
                aria-modal="true"
                aria-label={`Track order ${orderId}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="ffo-modal-head">
                    <div>
                        <div className="ffo-modal-eyebrow">Shipment tracking</div>
                        <div className="ffo-modal-title">Track order #{orderId}</div>
                    </div>
                    <button type="button" className="ffo-modal-close" onClick={onClose} aria-label="Close">✕</button>
                </div>

                {/* Body */}
                <div className="ffo-modal-body" style={{ paddingBottom: 26 }}>
                    {loading ? (
                        <div className="ffo-empty-inline">
                            <span className="ffo-spin" style={{ borderColor: "var(--ff-line)", borderTopColor: "var(--ff-gold-deep)", width: 26, height: 26 }} />
                            <p style={{ marginTop: 12 }}>Fetching latest updates…</p>
                        </div>
                    ) : error ? (
                        <div className="ffo-empty-inline">
                            <i className="bi bi-exclamation-circle" style={{ color: "var(--ff-danger)" }} />
                            <p style={{ margin: "10px 0 16px" }}>{error}</p>
                            <button type="button" className="ffh-btn" onClick={fetchTracking}>
                                <i className="bi bi-arrow-clockwise" /> Retry
                            </button>
                        </div>
                    ) : trackingData && trackingData.success ? (
                        <>
                            {/* Status hero */}
                            <div className={`ffo-track-hero${isDelivered ? " is-delivered" : ""}`}>
                                <div className="ffo-track-icon">{getStatusIcon(summary?.current_status)}</div>
                                <h3 className="ffo-track-status">{summary?.current_status || "Unknown status"}</h3>
                                <p className="ffo-track-courier">{summary?.courier_name || "Courier"}</p>

                                {summary?.edd && !isDelivered && (
                                    <span className="ffo-track-pill">📅 Expected: {formatDateTime(summary.edd)}</span>
                                )}
                                {summary?.delivered_date && (
                                    <span className="ffo-track-pill">✅ Delivered: {formatDateTime(summary.delivered_date)}</span>
                                )}
                            </div>

                            {/* Shipment details */}
                            <div className="ffo-track-section">
                                <h4 className="ffo-track-title">Shipment details</h4>
                                <div className="ffo-detail-grid">
                                    <DetailBox icon="bi-qr-code" label="AWB" value={summary?.awb_code} />
                                    {summary?.weight && <DetailBox icon="bi-box-seam" label="Weight" value={`${summary.weight} kg`} />}
                                    <DetailBox icon="bi-geo-alt" label="From" value={summary?.origin} />
                                    <DetailBox icon="bi-geo-alt-fill" label="To" value={summary?.destination} />
                                    {summary?.consignee_name && <DetailBox icon="bi-person" label="Consignee" value={summary.consignee_name} wide />}
                                    {summary?.pickup_date && <DetailBox icon="bi-calendar-check" label="Pickup" value={formatDateTime(summary.pickup_date)} wide />}
                                </div>
                            </div>

                            {/* Timeline */}
                            <div className="ffo-track-section">
                                <h4 className="ffo-track-title">
                                    Tracking timeline
                                    <span>{timeline.length} updates</span>
                                </h4>

                                {timeline.length > 0 ? (
                                    <div className="ffo-timeline">
                                        {timeline.map((activity, index) => (
                                            <div key={index} className={`ffo-tl-item${index === 0 ? " is-first" : ""}`}>
                                                <div className="ffo-tl-mark">
                                                    <div className="ffo-tl-dot" />
                                                    {index < timeline.length - 1 && <div className="ffo-tl-line" />}
                                                </div>
                                                <div className="ffo-tl-card">
                                                    <p>{activity.activity || "Update"}</p>
                                                    {activity.location && (
                                                        <small><i className="bi bi-geo-alt" />{activity.location}</small>
                                                    )}
                                                    {activity.date && (
                                                        <small><i className="bi bi-clock" />{formatDateTime(activity.date)}</small>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="ffo-empty-inline">
                                        <i className="bi bi-clock-history" />
                                        <p style={{ marginTop: 8 }}>No tracking updates yet</p>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="ffo-empty-inline">
                            <p>No tracking data available</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="ffo-modal-foot">
                    <button type="button" className="ffh-btn ffh-btn--outline" onClick={onClose}>Close</button>
                    {trackingData?.track_url && (
                        <a
                            href={trackingData.track_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ffh-btn ffh-btn--outline"
                        >
                            <i className="bi bi-box-arrow-up-right" /> Open in browser
                        </a>
                    )}
                    <button type="button" className="ffh-btn" onClick={fetchTracking}>
                        <i className="bi bi-arrow-clockwise" /> Refresh
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TrackingModal;